import React from 'react';
import { useHistory } from 'react-router-dom';
import { Order } from '../../types/payment.types';
import { resolveChainIcon } from '../../variables/chainIcons';
import { CURRENCY_SYMBOLS } from '../../variables/currencies';
import { useI18n } from '../../i18n/I18n';

interface GroupedOrders {
    date: string;
    orders: Order[];
}

interface TransactionListProps {
    readonly loading: boolean;
    readonly hasSettings: boolean;
    readonly groupedOrders: GroupedOrders[];
    readonly filteredOrders: Order[];
    readonly visibleCount: number;
    readonly onShowMore: () => void;
}

const getWalletSuffix = (address: string) =>
    address ? `.${address.slice(-3).toLowerCase()}` : '···';

const parseDateArray = (dateArray: any): Date | null => {
    if (!dateArray) return null;
    if (Array.isArray(dateArray)) {
        return new Date(
            parseInt(dateArray[0]),
            parseInt(dateArray[1]) - 1,
            parseInt(dateArray[2]),
            parseInt(dateArray[3] ?? 0),
            parseInt(dateArray[4] ?? 0)
        );
    }
    const d = new Date(dateArray);
    return isNaN(d.getTime()) ? null : d;
};

const formatTime = (dateArray: any) => {
    const d = parseDateArray(dateArray);
    if (!d) return '';
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
};

export default function TransactionList({
    loading,
    hasSettings,
    groupedOrders,
    filteredOrders,
    visibleCount,
    onShowMore,
}: TransactionListProps) {
    const { t } = useI18n();
    const history = useHistory();

    if (loading) {
        return (
            <div className="animate-pulse">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="px-6 py-4 flex items-center gap-4" style={{ borderBottom: '1px solid var(--divider)' }}>
                        <div className="w-11 h-11 rounded-full flex-shrink-0" style={{ background: 'var(--color-surface)' }} />
                        <div className="flex-1 space-y-2">
                            <div className="h-3.5 rounded-full w-3/5" style={{ background: 'var(--color-surface)' }} />
                            <div className="h-2.5 rounded-full w-2/5" style={{ background: 'var(--color-surface)' }} />
                        </div>
                        <div className="space-y-2 text-right">
                            <div className="h-3.5 rounded-full w-16 ml-auto" style={{ background: 'var(--color-surface)' }} />
                            <div className="h-2.5 rounded-full w-12 ml-auto" style={{ background: 'var(--color-surface)' }} />
                        </div>
                    </div>
                ))}
                {/* "Ver más" button placeholder */}
                <div className="px-6 py-4 flex justify-center" style={{ borderTop: '1px solid var(--divider)' }}>
                    <div className="h-9 rounded-full w-32" style={{ background: 'var(--color-surface)' }} />
                </div>
            </div>
        );
    }

    if (filteredOrders.length === 0) {

        return (
            <div className="px-6 py-8 flex w-full">
                <div
                    className="w-full rounded-[28px] p-8 flex flex-col items-center justify-center text-center mx-auto max-w-sm"
                    style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                >
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-border)' }}>
                        <span className="material-symbols-outlined text-[40px] text-txt-secondary">
                            point_of_sale
                        </span>
                    </div>
                    <h3 className="text-xl font-bold text-txt-heading mb-3 tracking-tight">
                        No hay transacciones aún
                    </h3>
                    <p className="text-[15px] text-txt-secondary leading-relaxed mb-8">
                        Comenzá a vender creando tu primer<br />Punto de Venta (POS)
                    </p>
                    <button
                        onClick={() => history.push('/admin/pos')}
                        className="w-full bg-electric-blue hover:bg-electric-blue/90 text-white font-bold py-4 rounded-[18px] shadow-lg shadow-electric-blue/20 transition-all active:scale-95 flex items-center justify-center gap-2 border-none cursor-pointer text-[16px]"
                    >
                        <span className="material-symbols-outlined text-[20px]">add_circle</span>
                        Crear mi primer POS
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {groupedOrders.map((group, groupIndex) => (
                <div key={groupIndex}>
                    <div className="px-6 py-2 text-[11px] font-bold text-slate-grey uppercase tracking-wider sticky top-0 backdrop-blur-sm z-10" style={{ background: 'var(--sticky-bg)', borderBottom: '1px solid var(--divider)' }}>
                        {group.date}
                    </div>
                    {group.orders.map((order, orderIndex) => {
                        const chainIcon = resolveChainIcon(order.chain);
                        const chainLabel = order.chain;
                        const hasFiat = order.fiatAmount != null && order.fiatCurrency;
                        const fiatCurrency = hasFiat ? order.fiatCurrency! : '';
                        const fiatSymbol = CURRENCY_SYMBOLS[fiatCurrency] ?? '';
                        const fiatAmount = hasFiat ? order.fiatAmount! : null;
                        const avatarColors = (groupIndex + orderIndex) % 2 === 0
                            ? 'bg-accent-purple'
                            : 'bg-electric-blue';

                        return (
                            <div key={order.orderId || orderIndex} className="group px-6 py-4 flex items-center justify-between cursor-pointer transition-colors hover:bg-surface-hover" style={{ borderBottom: '1px solid var(--divider)' }}>
                                {/* Avatar */}
                                <div className={`w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[13px] font-bold ${avatarColors}`}>
                                    {getWalletSuffix(order.recipient ?? '')}
                                </div>

                                {/* Middle info */}
                                <div className="flex flex-col flex-1 min-w-0 mx-4 justify-center">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-bold text-txt-heading text-[15px] leading-tight">
                                            <span className="block md:hidden">
                                                {(() => {
                                                    const name = order.concept || order.sessionType?.replace('_', ' ') || '';
                                                    return name.length > 17 ? name.slice(0, 17) + '...' : name;
                                                })()}
                                            </span>
                                            <span className="hidden md:inline">
                                                {order.concept || order.sessionType?.replace('_', ' ')}
                                            </span>
                                        </h3>
                                        {order.publicOrderId && (
                                            <span className="text-[12px] text-txt-secondary font-bold">
                                                ...{order.publicOrderId.slice(-3)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5 text-[12px] text-txt-secondary font-medium">
                                        <span>
                                            {order.sessionType === 'PAYMENT_LINK' ? 'POS' :
                                                order.sessionType === 'WOOCOMMERCE' ? 'WOO' :
                                                    order.sessionType === 'X402' ? 'x402 · Agent' :
                                                        order.sessionType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || t('home.order')}
                                        </span>
                                        <span className="text-txt-muted">·</span>
                                        <span>{formatTime(order.createdDate)}</span>
                                        {order.chain && (
                                            <>
                                                <span className="text-txt-muted">·</span>
                                                {chainIcon ? (
                                                    <img src={chainIcon} alt={chainLabel} className="w-3.5 h-3.5 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
                                                        <span className="text-[7px] text-txt-heading font-bold">
                                                            {chainLabel?.charAt(0).toUpperCase() ?? '?'}
                                                        </span>
                                                    </div>
                                                )}
                                                {order.receipts?.[0] ? (
                                                    <a
                                                        href={order.receipts[0]}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-0.5 hover:text-electric-blue transition-colors cursor-pointer capitalize"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {chainLabel}
                                                        <span className="material-symbols-outlined !text-[12px]">open_in_new</span>
                                                    </a>
                                                ) : (
                                                    <span className="capitalize">{chainLabel}</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Amount */}
                                <div className="text-right flex-shrink-0 flex flex-col justify-center">
                                    {fiatAmount != null ? (
                                        <span className="font-bold text-txt-heading text-[15px] leading-tight">
                                            {fiatSymbol}{fiatAmount.toLocaleString()}
                                            <span className="text-[11px] font-semibold ml-1 text-txt-secondary">{fiatCurrency}</span>
                                        </span>
                                    ) : order.amount != null && order.token ? (
                                        <span className="font-bold text-txt-heading text-[15px] leading-tight">
                                            {order.amount} <span className="text-[11px] font-semibold ml-1 text-txt-secondary">{order.token}</span>
                                        </span>
                                    ) : null}
                                    {fiatAmount != null && order.amount != null && order.token && (
                                        <span className="text-[12px] text-txt-secondary font-medium mt-0.5">
                                            {order.amount} {order.token}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}

            {visibleCount < filteredOrders.length && (
                <div className="px-6 py-4 flex justify-center" style={{ borderTop: '1px solid var(--divider)' }}>
                    <button
                        onClick={onShowMore}
                        className="glass-btn px-6 py-2.5 rounded-full flex items-center gap-2 text-[13px] font-semibold text-txt-secondary hover:text-txt-heading transition-all"
                    >
                        <span>{t('home.showMore')}</span>
                        <span className="material-symbols-outlined text-[16px]">expand_more</span>
                    </button>
                </div>
            )}
        </>
    );
}
